import { Box, LinearProgress } from "@mui/material";
import { SphereTooltip } from "@components/common/sphere-tooltip";
import { blue, Text } from "@flat-ui";

interface Props {
  /** THe maximum limit of usage */
  max?: number;

  /** The current usage */
  current: number;

  /** The current usage in percentage */
  percentage?: number;

  /** The unit of measurement for the current usage */
  currentUnit?: string;

  /** The unit of measurement for the max value */
  maxUnit?: string;

  /** The number of decimal places to display */
  decimalPlaces?: number;
}

/** Details of a subscription constraint */
export function UsageLimit({
  current,
  max,
  percentage = 0,
  currentUnit = "",
  maxUnit = "",
  decimalPlaces,
}: Props): JSX.Element {
  const maxToDisplay = decimalPlaces ? max?.toFixed(decimalPlaces) : max;
  const currentToDisplay = decimalPlaces
    ? current.toFixed(decimalPlaces)
    : current;

  return (
    <Box
      sx={{ width: "100%", display: "flex", alignItems: "center", gap: 3.5 }}
    >
      <Box>
        <Text variant="heading20">
          {currentToDisplay} {currentUnit}
        </Text>
        {max && <Text variant="bodyM">{` / ${maxToDisplay} ${maxUnit}`}</Text>}
      </Box>

      {max && (
        <SphereTooltip
          title={`${currentToDisplay} ${currentUnit} of ${maxToDisplay} ${maxUnit} used (${percentage.toFixed(
            decimalPlaces
          )}%)`}
          // eslint-disable-next-line @typescript-eslint/naming-convention -- defined in mui
          tooltipProps={{ placement: "top", followCursor: true }}
          boxProps={{ flexGrow: 1 }}
        >
          <LinearProgress
            sx={{
              height: "10px",
              width: "100%",
              backgroundColor: blue[100],
              "& .MuiLinearProgress-bar": {
                background: blue[400],
              },
            }}
            variant="determinate"
            value={Math.min(percentage, 100)}
          />
        </SphereTooltip>
      )}
    </Box>
  );
}
